import { Moment } from 'moment';
import { SelectOption } from 'global';
import { MUIBaseEntity } from 'components/shared/MUITable';

export enum ReportTypes {
  ApplicationReports = 'applicationReports',
  LenderPerformance = 'lenderPerformance',
}

export enum LenderPerformanceTab {
  ConversionFunnel = 'conversionFunnel',
  ApprovalTerms = 'approvalTerms',
}

export interface AsyncResource<Data> {
  data: Data;
  loading: boolean;
  error: string | null;
}

export interface LenderPerformanceFilterFormValues {
  from: Moment;
  to: Moment;
  lenders: SelectOption[];
  salesReps: SelectOption[];
  locations: SelectOption[];
}

export type RatioCardKey = 'lookToBook' | 'fundRate' | 'responseRate' | 'fundedVolume';

export interface RatioCardConfig {
  key: RatioCardKey;
  title: string;
  description: string;
  format: (value: number) => string;
}

export type FunnelStageKey = 'submitted' | 'decisioned' | 'approved' | 'funded';

export interface FunnelStageConfig {
  key: FunnelStageKey;
  label: string;
  color: string;
}

export type FunnelConversionKey = 'responseRate' | 'approvalRate' | 'fundRate';

export interface FunnelConversionConfig {
  key: FunnelConversionKey;
  label: string;
}

export enum ConversionTableColumnKey {
  LenderName = 'lenderName',
  Sent = 'sent',
  Approved = 'approved',
  ApprovalPct = 'approvalPct',
  Funded = 'funded',
  FundPct = 'fundPct',
  LookToBook = 'lookToBook',
  FundedAmount = 'fundedAmount',
}

export interface ConversionTableColumnConfig {
  key: ConversionTableColumnKey;
  label: string;
  align: 'left' | 'right';
  percent?: boolean;
  currency?: boolean;
  colored?: boolean;
}

export interface ConversionTableSorting {
  key: string;
  value: 'asc' | 'desc';
}

export interface ConversionTableRow extends MUIBaseEntity {
  id: string;
  lenderName: string;
  sent: number;
  approved: number;
  approvalPct: number;
  funded: number;
  fundPct: number;
  lookToBook: number;
  fundedAmount: number;
}

export interface LookToBookByLenderDatum {
  lender: string;
  lookToBook: number;
  approvalRate: number;
  approvalGap: number;
  [key: string]: string | number;
}

export interface MonthlyLookToBookDatum {
  month: string;
  approvalRate: number;
  fundRate: number;
  lookToBook: number;
  [key: string]: string | number;
}

export interface LookToBookHeatmapCell {
  month: string;
  value: number | null;
}

export interface LookToBookHeatmapRow {
  key: string;
  label: string;
  cells: LookToBookHeatmapCell[];
}

export interface LookToBookHeatmapDataset {
  months: string[];
  rows: LookToBookHeatmapRow[];
}

export type ApprovalTermsSummaryCardKey = 'approvedRate' | 'approvedTerm' | 'docFee';

export interface ApprovalTermsSummaryCardConfig {
  key: ApprovalTermsSummaryCardKey;
  title: string;
  formatValue: (value: number) => string;
  formatRange: (min: number, max: number) => string;
}

export enum TermsComparisonColumnKey {
  LenderName = 'lenderName',
  FundedAmount = 'fundedAmount',
  AvgRate = 'avgRate',
  RateRange = 'rateRange',
  AvgTerm = 'avgTerm',
  TermOptions = 'termOptions',
  DocFee = 'docFee',
}

export interface TermsComparisonColumnConfig {
  key: TermsComparisonColumnKey;
  label: string;
  align: 'left' | 'right';
  sortKey?: string;
}

export interface TermsComparisonRow extends MUIBaseEntity {
  id: string;
  lenderName: string;
  fundedAmount: number;
  avgRate: number;
  rateRange: string;
  avgTerm: number;
  termOptions: string;
  docFee: number;
}

export interface AverageRateTierConfig {
  label: string;
  color: string;
  max: number;
}

export interface TermDistributionBucketConfig {
  term: number;
  label: string;
  color: string;
}

export interface AverageRateByLenderDatum {
  lender: string;
  avgRate: number;
  [key: string]: string | number;
}

export interface TermDistributionByLenderDatum {
  lender: string;
  [key: string]: string | number;
}

export interface TotalCostByLenderDatum {
  lender: string;
  docFee: number;
  [key: string]: string | number;
}
